import { Calendar, Trash2 } from 'lucide-react';
import { formatDate } from '../../lib/formatters';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Badge,
  FormSelect,
  EmptyState,
} from '../ui';
import LoadingSpinner from '../ui/LoadingSpinner';

interface CalendarBooking {
  id: number;
  client_name: string;
  service_type_id: number;
  status: 'booked' | 'confirmed' | 'canceled';
  contact_id?: number;
  contact_name?: string;
  contact_info?: string;
  service_name?: string;
  professional_name?: string;
  slots?: {
    date: string;
    start_time: string;
    end_time: string;
  }[];
  booked_at?: string;
}

interface BookingListProps {
  bookings: CalendarBooking[];
  isLoading: boolean;
  isFiltered: boolean;
  onView: (booking: CalendarBooking) => void;
  onStatusChange: (booking: CalendarBooking, status: string) => void;
  onDelete: (booking: CalendarBooking) => void;
  onCreateNew: () => void;
}

function BookingList({
  bookings,
  isLoading,
  isFiltered,
  onView,
  onStatusChange,
  onDelete,
  onCreateNew,
}: BookingListProps) {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge variant="success">Confirmado</Badge>;
      case 'booked':
        return <Badge variant="warning">Reservado</Badge>;
      case 'canceled':
        return <Badge variant="error">Cancelado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <EmptyState
        icon={<Calendar className="h-12 w-12" />}
        title={isFiltered ? 'Nenhum agendamento encontrado' : 'Nenhum agendamento cadastrado'}
        description={
          isFiltered
            ? 'Tente ajustar os filtros para encontrar o que procura'
            : 'Crie seu primeiro agendamento para começar'
        }
        action={{
          label: 'Criar Agendamento',
          onClick: onCreateNew,
        }}
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Cliente</TableHead>
            <TableHead>Serviço</TableHead>
            <TableHead>Profissional</TableHead>
            <TableHead>Data/Hora</TableHead>
            <TableHead>Contato</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.map((booking: CalendarBooking) => (
            <TableRow key={booking.id}>
              <TableCell>{booking.client_name}</TableCell>
              <TableCell>{booking.service_name}</TableCell>
              <TableCell>{booking.professional_name}</TableCell>
              <TableCell>
                {booking.slots && booking.slots.length > 0
                  ? `${formatDate(booking.slots[0].date)} ${booking.slots[0].start_time || ''}`
                  : '-'}
              </TableCell>
              <TableCell>
                {booking.contact_name ? (
                  <div>
                    <div>{booking.contact_name}</div>
                    <div className="text-xs text-gray-500">{booking.contact_info}</div>
                  </div>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>{getStatusBadge(booking.status)}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end space-x-2">
                  <Button variant="ghost" onClick={() => onView(booking)} title="Visualizar">
                    <Calendar className="h-4 w-4 text-primary-600" />
                  </Button>
                  {booking.status !== 'canceled' && (
                    <FormSelect
                      value={booking.status}
                      onChange={e => onStatusChange(booking, e.target.value)}
                      className="w-32 text-sm"
                    >
                      <option value="booked">Reservado</option>
                      <option value="confirmed">Confirmado</option>
                      <option value="canceled">Cancelado</option>
                    </FormSelect>
                  )}
                  <Button variant="ghost" onClick={() => onDelete(booking)} title="Excluir">
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default BookingList;
